import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAtlas } from './store.ts';
import { useShortcuts } from './shortcuts.ts';
import { startClock } from './clock.ts';
import { startSims } from './sims.ts';
import { startLive } from './live.ts';
import { startViewport } from '../viewport/host.ts';
import { IconProvider, TipProvider } from '../ui/primitives.tsx';
import { TopBar } from '../features/TopBar.tsx';
import { LeftPanel } from '../features/LeftPanel.tsx';
import { Inspector } from '../features/Inspector.tsx';
import { Timeline } from '../features/Timeline.tsx';
import { Traces } from '../features/Traces.tsx';
import { Methods } from '../features/Methods.tsx';
import { Hud } from '../features/Hud.tsx';
import { Labels } from '../features/Labels.tsx';
import { ElectrodeSites } from '../features/ElectrodeSites.tsx';
import { Palette } from '../features/Palette.tsx';
import { Help } from '../features/Help.tsx';
import { GpuNotice } from '../features/GpuNotice.tsx';
import { HoverCard } from '../features/HoverCard.tsx';
import { Loading, PresentHint } from '../features/Loading.tsx';
import { SectionMarks } from '../features/SectionMarks.tsx';

const params = new URLSearchParams(location.search);

/** How much of the viewport each panel covers, so the camera frames the clear area. */
function useInsets(root: React.RefObject<HTMLDivElement | null>) {
  const setInsets = useAtlas((s) => s.setInsets);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => {
      if (useAtlas.getState().presenting) return setInsets({ top: 0, right: 0, bottom: 0, left: 0 });
      const base = el.getBoundingClientRect();
      const get = (sel: string) => {
        const p = el.querySelector<HTMLElement>(sel);
        return p && !p.classList.contains('collapsed') ? p.getBoundingClientRect() : null;
      };
      const left = get('.panel.left');
      const right = get('.panel.right');
      const top = get('.topbar');
      // The bottom inset clears the transport and, when it's open, the trace strip above it.
      const bottom = [get('.timeline'), get('.traces')].reduce<DOMRect | null>((a, r) => (r && (!a || r.top < a.top) ? r : a), null);
      setInsets({
        top: top ? Math.round(top.bottom - base.top) : 0,
        left: left ? Math.round(left.right - base.left) : 0,
        right: right ? Math.round(base.right - right.left) : 0,
        bottom: bottom ? Math.round(base.bottom - bottom.top) : 0,
      });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-presenting'] });
    measure();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [root, setInsets]);
}

export function App() {
  const root = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const scenario = useAtlas((s) => s.scenario);
  const presenting = useAtlas((s) => s.presenting);
  useShortcuts();
  useInsets(root);
  useEffect(() => {
    if (!canvas.current) return;
    void startViewport(canvas.current, { forceMock: params.get('engine') === 'mock' });
    startClock();
    startSims();
    startLive();
  }, []);
  return (
    <TipProvider>
      <IconProvider>
        <div ref={root} className="atlas" data-scenario={scenario} data-presenting={presenting}>
          <canvas ref={canvas} className="viewport" />
          <Loading />
          <ElectrodeSites />
          <Labels />
          <SectionMarks />
          <HoverCard />
          <TopBar />
          <LeftPanel />
          <Inspector />
          <div className="bottom">
            <Traces />
            <Methods />
            <Timeline />
            <Hud />
          </div>
          <PresentHint />
          <Palette />
          <Help />
          <GpuNotice />
        </div>
      </IconProvider>
    </TipProvider>
  );
}
